import { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import toast from 'react-hot-toast';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography
} from '@material-ui/core';
import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { formationApi } from '../../../api/formationApi';
import { questionnaireApi } from '../../../api/questionnaireApi';
import { getTone, grey, toneAlpha } from '../../../theme/tones';

/**
 * Import des trois questionnaires d'une session, depuis un CSV.
 *
 * Deux boutons : télécharger le modèle à remplir, et déposer le fichier
 * rempli. Le modèle et le lecteur côté serveur décrivent le même format —
 * `QuestionnaireModel::IMPORT_COLUMNS` doit rester aligné sur COLONNES.
 *
 * Le serveur refuse le fichier en entier à la première erreur et renvoie la
 * liste des lignes fautives : mieux vaut renvoyer l'utilisateur à son tableur
 * que de créer trois questionnaires dont un incomplet.
 */

/** Colonnes du modèle, dans l'ordre. Miroir de IMPORT_COLUMNS côté serveur. */
const COLONNES = [
  'type_questionnaire', 'nom_questionnaire', 'ordre',
  'question', 'type_question', 'options', 'poids', 'bonnes_reponses', 'points'
];

/**
 * Séparateur des options dans une cellule.
 *
 * Ni la virgule ni le point-virgule, qui servent au CSV lui-même et
 * apparaissent constamment dans les intitulés de réponses.
 */
const SEP = '|';

/**
 * Le modèle, pré-rempli d'un exemple par type.
 *
 * Les exemples ne sont pas décoratifs : ils montrent le seul point qui se
 * trompe en pratique — une bonne réponse doit être recopiée à l'identique
 * depuis les options, sinon le barème donne zéro à tout le monde.
 */
const EXEMPLES = [
  ['Pré-Formation', 'Pré-formation — à renommer', '1',
    'Quels signes évoquent une hypoglycémie ?', 'mcq',
    `Sueurs, tremblements${SEP}Soif intense${SEP}Confusion`,
    '', `Sueurs, tremblements${SEP}Confusion`, '2'],
  ['Pré-Formation', '', '2',
    'Quelle est votre expérience du sujet ?', 'open', '', '', '', ''],
  ['Post-Formation', 'Post-formation — à renommer', '1',
    'Quels signes évoquent une hypoglycémie ?', 'mcq',
    `Sueurs, tremblements${SEP}Soif intense${SEP}Confusion`,
    '', `Sueurs, tremblements${SEP}Confusion`, '2'],
  // Satisfaction : pas de bonne réponse ni de barème. C'est le poids de
  // l'option choisie, de 0 à 100, qui fait le taux affiché dans le CRM.
  ['Satisfaction', 'Satisfaction — à renommer', '1',
    'Comment jugez-vous la qualité de la formation ?', 'mcq',
    `Très satisfaisant${SEP}Satisfaisant${SEP}Peu satisfaisant${SEP}Insatisfaisant`,
    `100${SEP}66${SEP}33${SEP}0`, '', ''],
  ['Satisfaction', '', '2',
    'Recommanderiez-vous cette formation ?', 'mcq',
    `Oui${SEP}Non`, `100${SEP}0`, '', ''],
  ['Satisfaction', '', '3', 'Vos remarques libres', 'open', '', '', '', '']
];

/** Échappe une cellule pour le CSV : guillemets doublés si nécessaire. */
const cellule = (v) => {
  const s = String(v == null ? '' : v);
  return /[";\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const QuestionnaireImport = ({ onImported }) => {
  const [ouvert, setOuvert] = useState(false);
  const [formations, setFormations] = useState([]);
  const [formation, setFormation] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [session, setSession] = useState(null);
  const [fichier, setFichier] = useState(null);
  const [csv, setCsv] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [erreurs, setErreurs] = useState([]);
  const inputRef = useRef(null);

  const accent = getTone('indigo');

  /**
   * Le modèle à remplir. Point-virgule comme séparateur et BOM UTF-8 en tête :
   * sans eux, Excel en français ouvre tout dans une seule colonne et casse les
   * accents.
   */
  const telechargerModele = () => {
    const lignes = [COLONNES, ...EXEMPLES].map((l) => l.map(cellule).join(';'));
    const blob = new Blob([`﻿${lignes.join('\r\n')}\r\n`], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'modele-import-questionnaires.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  useEffect(() => {
    if (!ouvert || formations.length > 0) return;
    (async () => {
      try {
        const data = await formationApi.getFormationOptions(window.localStorage.getItem('accessToken'));
        setFormations(data || []);
      } catch (err) {
        console.error(err);
        toast.error('Impossible de charger les formations.');
      }
    })();
  }, [ouvert, formations.length]);

  useEffect(() => {
    setSession(null);
    if (!formation) {
      setSessions([]);
      return;
    }
    (async () => {
      try {
        const data = await questionnaireApi.getSessionsByFormationId(
          formation.id,
          window.localStorage.getItem('accessToken')
        );
        setSessions(data || []);
      } catch (err) {
        console.error(err);
        setSessions([]);
      }
    })();
  }, [formation]);

  const fermer = () => {
    setOuvert(false);
    setFormation(null);
    setSessions([]);
    setSession(null);
    setFichier(null);
    setCsv('');
    setErreurs([]);
    if (inputRef.current) inputRef.current.value = '';
  };

  const choisirFichier = (e) => {
    const f = e.target.files && e.target.files[0];
    setErreurs([]);
    if (!f) {
      setFichier(null);
      setCsv('');
      return;
    }
    setFichier(f);
    const reader = new FileReader();
    reader.onload = () => setCsv(String(reader.result || ''));
    reader.onerror = () => {
      toast.error('Le fichier n’a pas pu être lu.');
      setFichier(null);
      setCsv('');
    };
    // Lu en UTF-8 : le BOM éventuel d'Excel est retiré côté serveur.
    reader.readAsText(f, 'UTF-8');
  };

  const importer = async () => {
    if (!session || !csv) return;
    setEnvoi(true);
    setErreurs([]);
    try {
      const res = await questionnaireApi.importQuestionnaires(
        { csv, formation_id: formation ? formation.id : null, session_id: session.id },
        window.localStorage.getItem('accessToken')
      );
      if (!res || res.status !== 200) {
        setErreurs((res && res.errors) || []);
        toast.error((res && res.message) || "L'import a échoué.");
        return;
      }
      toast.success(res.message || 'Questionnaires importés.');
      if (onImported) onImported();
      fermer();
    } catch (err) {
      console.error(err);
      toast.error("L'import a échoué.");
    } finally {
      setEnvoi(false);
    }
  };

  const libelleSession = useMemo(() => (s) => {
    if (!s) return '';
    const date = s.start_date ? new Date(s.start_date).toLocaleDateString('fr-FR') : 'date à définir';
    return `N° ${s.number} • ${date}${s.status ? ` • ${s.status}` : ''}`;
  }, []);

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<DownloadIcon fontSize="small" />}
        onClick={telechargerModele}
        sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 800, borderColor: 'divider', color: 'text.primary' }}
      >
        Modèle CSV
      </Button>
      <Button
        variant="outlined"
        startIcon={<UploadFileIcon fontSize="small" />}
        onClick={() => setOuvert(true)}
        sx={{
          borderRadius: '12px',
          textTransform: 'none',
          fontWeight: 800,
          borderColor: accent.border,
          color: accent.color,
          bgcolor: accent.bg
        }}
      >
        Importer
      </Button>

      <Dialog open={ouvert} onClose={fermer} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '24px', p: 1 } }}>
        <DialogTitle sx={{ fontWeight: 900 }}>Importer des questionnaires</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2.5 }}>
            Un seul fichier porte les trois questionnaires d’une session : pré-formation,
            post-formation et satisfaction. Chaque ligne est une question, la colonne
            <strong> type_questionnaire</strong> indique à quel questionnaire elle appartient.
            Sur la satisfaction, ce n’est pas la colonne <strong>points</strong> qui compte
            mais <strong>poids</strong> : un nombre de 0 à 100 par option, dont la moyenne
            donne le taux affiché dans le CRM.
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <Autocomplete
              options={formations}
              value={formation}
              onChange={(e, v) => setFormation(v)}
              getOptionLabel={(o) => (o ? `${o.name}${o.programNumberAll ? ` (N° ${o.programNumberAll})` : ''}` : '')}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              renderInput={(params) => (
                <TextField {...params} label="Formation" variant="outlined" sx={{ '& .MuiOutlinedInput-root': { borderRadius: '14px' } }} />
              )}
            />
            <Autocomplete
              options={sessions}
              value={session}
              disabled={!formation}
              onChange={(e, v) => setSession(v)}
              getOptionLabel={libelleSession}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              noOptionsText="Aucune session pour cette formation"
              renderInput={(params) => (
                <TextField {...params} label="Session" variant="outlined" sx={{ '& .MuiOutlinedInput-root': { borderRadius: '14px' } }} />
              )}
            />

            <Box
              onClick={() => inputRef.current && inputRef.current.click()}
              sx={{
                p: 2.5,
                borderRadius: '16px',
                border: `1px dashed ${fichier ? accent.border : grey(300)}`,
                bgcolor: fichier ? toneAlpha('indigo', 0.06) : 'transparent',
                textAlign: 'center',
                cursor: 'pointer'
              }}
            >
              <UploadFileIcon sx={{ fontSize: 26, color: fichier ? accent.color : grey(400) }} />
              <Typography sx={{ mt: 0.75, fontWeight: 800, fontSize: '0.9rem' }}>
                {fichier ? fichier.name : 'Choisir le fichier CSV rempli'}
              </Typography>
              <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                {fichier ? 'Cliquez pour en choisir un autre' : 'Téléchargez d’abord le modèle si ce n’est pas fait'}
              </Typography>
              <input ref={inputRef} type="file" accept=".csv,text/csv" hidden onChange={choisirFichier} />
            </Box>

            {erreurs.length > 0 && (
              <Alert severity="error" sx={{ borderRadius: '14px' }}>
                <Typography sx={{ fontWeight: 800, fontSize: '0.85rem', mb: 0.5 }}>
                  Rien n’a été importé. À corriger dans le fichier :
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2.5, maxHeight: 220, overflow: 'auto' }}>
                  {erreurs.map((e) => (
                    <li key={e}><Typography sx={{ fontSize: '0.82rem' }}>{e}</Typography></li>
                  ))}
                </Box>
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={fermer} color="inherit" sx={{ fontWeight: 700 }}>Annuler</Button>
          <Button
            onClick={importer}
            variant="contained"
            disabled={!session || !csv || envoi}
            sx={{ borderRadius: '12px', px: 3, fontWeight: 800 }}
          >
            {envoi ? 'Import en cours…' : 'Importer'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

QuestionnaireImport.propTypes = {
  onImported: PropTypes.func
};

export default QuestionnaireImport;
